import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  RefreshCw,
  Download,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../../services/api';
import type { EmployeeAttendanceRecord } from '../../types/analytics';
import { MAX_ANALYTICS_DATE_RANGE_DAYS, clampToDate } from '../../../../shared/utils/dateUtils';
import { DateRangePicker } from '../../components/analytics';

interface KVInfo {
  kvId: number;
  kvName: string;
  roName: string;
  stateName: string;
  districtName: string;
  city?: string;
  latitude?: string;
  longitude?: string;
  address?: string;
  phone?: string;
  email?: string;
  principalName?: string;
}

interface KVStats {
  totalStaff: number;
  regularStaff: number;
  contractualStaff: number;
  presentCount: number;
  absentCount: number;
  noResponseCount: number;
  attendancePercent: number;
}



const KVAttendanceDetail: React.FC = () => {
  const { kvId } = useParams<{ kvId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [kvInfo, setKvInfo] = useState<KVInfo | null>(null);
  const [stats, setStats] = useState<KVStats | null>(null);
  const [employees, setEmployees] = useState<EmployeeAttendanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;

  // Filters
  const [empType, setEmpType] = useState<'regular' | 'contractual'>('regular');
  const [searchQuery, setSearchQuery] = useState('');

  // From / To date state
  // Priority: location.state (from prev screen) > searchParams > today
  const today = new Date().toISOString().split('T')[0];
  const routeState = (location.state as any) || {};
  const initialFrom: string = routeState.fromDate || searchParams.get('fromDate') || today;
  const initialTo: string = clampToDate(
    initialFrom,
    routeState.toDate || searchParams.get('toDate') || today
  );
  const [fromDate, setFromDate] = useState<string>(initialFrom);
  const [toDate, setToDate] = useState<string>(initialTo);

  const carouselImages = [img1];

  // Transform API employee data to our format (updated for new API response)
  const transformEmployeeData = useCallback((
    apiData: any[],
    empTypeFilter: 'regular' | 'contractual'
  ): EmployeeAttendanceRecord[] => {
    return apiData.map((emp: any) => {
      const attendancePct = Number(emp.attendancePercentage) || 0;

      const status: 'present' | 'absent' | 'no-response' =
        attendancePct > 0 ? 'present' : 'absent';

      const genderValue = emp.empGender === 1 || emp.gender === 1 ? 'Male' : emp.empGender === 2 || emp.gender === 2 ? 'Female' : 'Unknown';

      return {
        empId: emp.empID,
        empCode: emp.empCode || '',
        empName: emp.empName || '',
        empType: empTypeFilter,
        post: emp.empPost ? `Post ${emp.empPost}` : 'Staff',
        subject: emp.subject && emp.subject !== 0 ? `Subject ${emp.subject}` : '',
        gender: genderValue,
        kvId: Number(kvId),
        kvName: '',
        roName: '',
        stateName: '',
        status,
        inTime: fromDate || undefined,
        outTime: toDate || undefined,
        attendancePercent: attendancePct,
        totalDays: Number(emp.totalDays) || 0,
        presentDays: Number(emp.totalAttendance) || 0
      };
    });
  }, [fromDate, toDate, kvId]);

  // Load KV data from API
  const loadData = useCallback(async (page: number = 1) => {
    if (!kvId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch employee data
      const employeeResponse = await attendanceApi.getEmployeeAttendanceByKV(
        page,
        Number(kvId),
        fromDate,
        toDate,
        empType
      );

      if (employeeResponse && employeeResponse.success && employeeResponse.data) {
        const employeeData = employeeResponse.data;
        const pagination = employeeResponse.pagination;

        // KV info: populate from URL params on first load
        if (!kvInfo) {
          setKvInfo({
            kvId: Number(kvId),
            kvName: searchParams.get('kvName') || '',
            roName: searchParams.get('roName') || '',
            stateName: searchParams.get('stateName') || '',
            districtName: searchParams.get('districtName') || ''
          });
        }

        // Transform employee data
        const transformedEmps = transformEmployeeData(employeeData, empType);

        // Calculate stats from employee data
        const totalStaff = pagination?.total ?? transformedEmps.length;
        const presentCount = transformedEmps.filter(e => e.status === 'present').length;
        const absentCount = transformedEmps.filter(e => e.status === 'absent').length;
        const noResponseCount = transformedEmps.filter(e => e.status === 'no-response').length;
        const attendancePercent = transformedEmps.length > 0
          ? Math.round((presentCount / transformedEmps.length) * 100) : 0;

        setStats({
          totalStaff,
          regularStaff: empType === 'regular' ? totalStaff : 0,
          contractualStaff: empType === 'contractual' ? totalStaff : 0,
          presentCount,
          absentCount,
          noResponseCount,
          attendancePercent
        });

        // Use server-side pagination
        setEmployees(transformedEmps);
        setTotalRecords(pagination?.total ?? transformedEmps.length);
        setTotalPages(pagination?.totalPages ?? 1);
        setCurrentPage(pagination?.page ?? page);
      } else {
        // No data found
        setStats(null);
        setEmployees([]);
      }

    } catch (err) {
      console.error('Error loading KV data:', err);
      setError('Failed to load KV data. Please try again.');
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  }, [kvId, empType, fromDate, toDate, transformEmployeeData, kvInfo, searchParams]);

  // Load data on mount and when filters change
  useEffect(() => {
    loadData(1);
  }, [loadData]);

  // Sync URL search params with local date range state
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (fromDate) next.set('fromDate', fromDate);
      if (toDate) next.set('toDate', toDate);
      return next;
    });
  }, [fromDate, toDate, setSearchParams]);

  // Filter employees locally by search query
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;

    const query = searchQuery.toLowerCase();
    return employees.filter(emp =>
      emp.empName?.toLowerCase().includes(query) ||
      emp.post?.toLowerCase().includes(query) ||
      emp.subject?.toLowerCase().includes(query) ||
      String(emp.empId).includes(query)
    );
  }, [employees, searchQuery]);

  // Handle view employee detail - use empCode for regular, empId for contractual
  // Navigate to AttendanceRegister screen
  const handleViewEmployee = (emp: EmployeeAttendanceRecord) => {
    const identifier = empType === 'regular' ? (emp.empCode || emp.empId) : emp.empId;
    const profileUrl = empType === 'contractual'
      ? `/attendance/employee/${identifier}?emptype=contractual`
      : `/attendance/employee/${identifier}`;
    navigate(profileUrl, {
      state: { from: `/attendance/analytics/kv/${kvId}`, employee: emp, kvInfo, empType }
    });
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadData(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Export to CSV
  const handleExport = () => {
    if (!filteredEmployees.length) return;

    const headers = ['#', 'Emp ID', 'Name', 'Type', 'Gender', 'Attendance %', 'Days Present', 'Total Days'];
    const rows = filteredEmployees.map((emp, idx) => [
      idx + 1,
      emp.empId,
      emp.empName,
      emp.empType,
      emp.gender || '',
      `${emp.attendancePercent || 0}%`,
      emp.presentDays || 0,
      emp.totalDays || 0
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kvInfo?.kvName || 'kv'}-employees-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get status color/badge
  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'present':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Present</span>;
      case 'absent':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Absent</span>;
      case 'leave':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Leave</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">No Response</span>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/attendance/analytics')}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#6A1786]">
                {kvInfo?.kvName || 'Loading...'}
              </h1>
              {kvInfo && (
                <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {[kvInfo.districtName, kvInfo.stateName, kvInfo.roName].filter(Boolean).join(' • ')}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => loadData(currentPage)}
              disabled={isLoading}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExport}
              disabled={!employees.length}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
              title="Export to CSV"
            >
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* KV Info Card */}
        {kvInfo && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-wrap items-start gap-6">
              {/* School Icon */}
              <div className="p-4 bg-[#6A1786]/10 rounded-xl">
                <Building2 className="w-10 h-10 text-[#6A1786]" />
              </div>

              {/* School Details */}
              <div className="flex-1 min-w-[200px]">
                <h2 className="text-lg font-semibold text-gray-900">{kvInfo.kvName}</h2>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{kvInfo.districtName}, {kvInfo.stateName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span>Region: {kvInfo.roName}</span>
                  </div>
                  {kvInfo.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{kvInfo.phone}</span>
                    </div>
                  )}
                  {kvInfo.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span>{kvInfo.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              {stats && (
                <div className="flex flex-wrap gap-3">
                  <div className="px-4 py-2 bg-purple-50 rounded-lg text-center min-w-[80px]">
                    <p className="text-lg font-bold text-purple-700">{stats.totalStaff}</p>
                    <p className="text-xs text-purple-600">Total Staff</p>
                  </div>
                  <div className="px-4 py-2 bg-green-50 rounded-lg text-center min-w-[80px]">
                    <p className="text-lg font-bold text-green-700">{stats.presentCount}</p>
                    <p className="text-xs text-green-600">Present</p>
                  </div>
                  <div className="px-4 py-2 bg-red-50 rounded-lg text-center min-w-[80px]">
                    <p className="text-lg font-bold text-red-700">{stats.absentCount}</p>
                    <p className="text-xs text-red-600">Absent</p>
                  </div>
                  <div className="px-4 py-2 bg-blue-50 rounded-lg text-center min-w-[80px]">
                    <p className="text-lg font-bold text-blue-700">{stats.attendancePercent}%</p>
                    <p className="text-xs text-blue-600">Attendance</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date Range Picker */}
            <div className="flex items-center gap-3">
              <DateRangePicker
                value={{ fromDate, toDate }}
                onSubmit={(range) => {
                  if (range.fromDate && range.toDate) {
                    setFromDate(range.fromDate);
                    setToDate(range.toDate);
                  }
                }}
                disabled={isLoading}
              />
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg font-medium whitespace-nowrap">
                ⚠️ Max {MAX_ANALYTICS_DATE_RANGE_DAYS} days
              </span>
            </div>

            {/* Employee Type Filter */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setEmpType('regular')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${empType === 'regular' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Regular
              </button>
              <button
                onClick={() => setEmpType('contractual')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${empType === 'contractual' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Contractual
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            <p className="font-medium">Error loading data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Employee Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Gender</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Attendance %</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Days Present</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total Working Days</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="animate-pulse">
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-6"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-14 mx-auto"></div></td>
                      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-14 mx-auto"></div></td>
                      <td className="px-4 py-4"><div className="h-6 bg-gray-200 rounded w-16 mx-auto"></div></td>
                    </tr>
                  ))
                ) : filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-lg">No employees found</p>
                      <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((emp, idx) => (
                    <tr
                      key={emp.empId}
                      onClick={() => handleViewEmployee(emp)}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {(currentPage - 1) * pageSize + idx + 1}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{emp.empName}</p>
                          <p className="text-xs text-gray-500">ID: {emp.empId}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${emp.empType === 'contractual'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-purple-100 text-purple-700'
                          }`}>
                          {emp.empType === 'contractual' ? 'Contractual' : 'Regular'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {emp.gender || '-'}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`text-sm font-semibold ${(emp.attendancePercent || 0) >= 85 ? 'text-green-600' :
                          (emp.attendancePercent || 0) >= 75 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                          {emp.attendancePercent || 0}%
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-gray-600 font-medium text-green-600">
                        {emp.presentDays ?? '-'}
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-gray-600">
                        {emp.totalDays ?? '-'}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewEmployee(emp);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Detail
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default KVAttendanceDetail;
